/**
 * WhatsAppTemplateService
 * WhatsApp templates in the Communications API (route api/whatsapp-templates): the template library synced from
 * 360dialog, the CCMS messages and their links to approved Meta templates, the template creator (rule checker, submit
 * to Meta) and "Suggest wording" (Claude, behind the WhatsAppTemplateWording AI flag).
 *
 * The forms* methods use api/whatsapp-templates/forms, which Forms admins (Forms.Settings.Manage) may call without
 * Communications rights; they only touch the Forms messages (TemplateIDs 61000-61099).
 * Every method unwraps ResponseData and throws CCMSError on a non-2xx answer (its errors list the problems).
 * Added in SDK 0.0.92.
 */
import { BaseService } from '../base/BaseService';
export class WhatsAppTemplateService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? WhatsAppTemplateService.DEFAULT_SERVICE_PATH,
            basePath: WhatsAppTemplateService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async unwrap(promise, fallback) {
        const response = await promise;
        if (response.data === undefined || response.data === null)
            throw new Error(response.message || fallback);
        return response.data;
    }
    // ==================== LIBRARY ====================
    /** GET library: every synced template (approvedOnly: only Approved ones). */
    getLibrary(approvedOnly = false) {
        const url = this.buildUrl(`library${approvedOnly ? '?approvedOnly=true' : ''}`);
        return this.unwrap(this.http.get(url), 'Failed to load the WhatsApp templates');
    }
    /** GET library/{id} */
    getTemplate(id) {
        return this.unwrap(this.http.get(this.buildUrl(`library/${id}`)), 'Template not found');
    }
    /** POST library/sync: read every template from 360dialog now. */
    syncLibrary() {
        return this.unwrap(this.http.post(this.buildUrl('library/sync')), 'Refresh failed');
    }
    // ==================== MESSAGES AND LINKS ====================
    /** GET messages: the CCMS messages (optionally one module, e.g. FormsManagement) with fields and links. */
    getMessages(module, refresh = false) {
        const params = new URLSearchParams();
        if (module)
            params.set('module', module);
        if (refresh)
            params.set('refresh', 'true');
        const qs = params.toString();
        return this.unwrap(this.http.get(this.buildUrl(`messages${qs ? `?${qs}` : ''}`)), 'Failed to load the messages');
    }
    /** GET messages/{templateId} */
    getMessage(templateId) {
        return this.unwrap(this.http.get(this.buildUrl(`messages/${templateId}`)), 'Message not found');
    }
    /** PUT links/{templateId}: link a message to an approved template and map its variables. */
    saveLink(templateId, request) {
        return this.unwrap(this.http.put(this.buildUrl(`links/${templateId}`), request), 'Saving the link failed');
    }
    /** DELETE links/{templateId}: unlink. */
    deleteLink(templateId) {
        return this.unwrap(this.http.delete(this.buildUrl(`links/${templateId}`)), 'Unlinking failed');
    }
    // ==================== CREATOR ====================
    /** POST creator/check: the rule checker (errors block submit; warnings do not). */
    checkDraft(draft) {
        return this.unwrap(this.http.post(this.buildUrl('creator/check'), draft), 'Check failed');
    }
    /** POST creator/submit: submit to Meta through 360dialog; it shows as Pending and links itself to its message. */
    submit(request) {
        return this.unwrap(this.http.post(this.buildUrl('creator/submit'), request), 'Submitting failed');
    }
    /** GET creator/ai: is "Suggest wording" on (and why not)? */
    getAiAvailability() {
        return this.unwrap(this.http.get(this.buildUrl('creator/ai')), 'AI availability failed');
    }
    /** POST creator/suggest: Claude suggests wording (no personal data is sent). */
    suggestWording(request) {
        return this.unwrap(this.http.post(this.buildUrl('creator/suggest'), request), 'Suggest wording failed');
    }
    // ==================== FORMS (Forms admins) ====================
    /** GET forms/messages: the nine Forms messages with their links. */
    getFormsMessages() {
        return this.unwrap(this.http.get(this.buildUrl('forms/messages')), 'Failed to load the Forms messages');
    }
    /** GET forms/library: the templates a Forms message can be linked to (approved ones; all: every template). */
    getFormsLibrary(all = false) {
        return this.unwrap(this.http.get(this.buildUrl(`forms/library${all ? '?all=true' : ''}`)), 'Failed to load the WhatsApp templates');
    }
    /** PUT forms/links/{templateId} (Forms messages only). */
    saveFormsLink(templateId, request) {
        return this.unwrap(this.http.put(this.buildUrl(`forms/links/${templateId}`), request), 'Saving the link failed');
    }
    /** DELETE forms/links/{templateId} (Forms messages only). */
    deleteFormsLink(templateId) {
        return this.unwrap(this.http.delete(this.buildUrl(`forms/links/${templateId}`)), 'Unlinking failed');
    }
}
WhatsAppTemplateService.DEFAULT_SERVICE_PATH = 'communications';
WhatsAppTemplateService.BASE_PATH = 'api/whatsapp-templates';
//# sourceMappingURL=WhatsAppTemplateService.js.map